import React, {useState} from 'react';
import {
  Modal,
  Text,
  View,
  Image,
  TextInput,
  useWindowDimensions,
  TextStyle,
  ImageStyle,
  ViewStyle,
  TouchableOpacity,
  NativeSyntheticEvent,
  TextInputChangeEventData,
} from 'react-native';
import {colors} from 'theme/Colors';
import {Button} from 'ui-kits';
import {corner_radius, spacing} from 'theme/Units';
import {AnimatedCircle} from 'components/common';
import {preBooking} from 'services/api';
import { logPreBookingEvent } from 'services/firebase';
import {getSuccessMessage, getErrorMessage} from 'utils/getMessages';
import {SUCCESS_KEY, ERROR_KEY} from 'constants/keys';
import { useToast } from 'hooks';
import {staticDataForScreen} from 'constants/DefaultValues';

const ENTITIES = staticDataForScreen.landing_screen.signupModalAnimatedCircles;

type propTypes = Readonly<{
  visible: boolean;
  onClose: () => void;
}>;

export function SignUpModal(props: propTypes) {
  const {onClose, visible} = props;
  // states for api
  const [error, setError] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  // for toast messages
  const {showToasts} = useToast();

  // function validate email and return tru false based on that
  function validateEmail(): boolean {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  }

  // event handler to handle join waitlist functionality
  async function handleSubmit(): Promise<void> {
    setError('');

    // if no value passed for email, throw error
    if (email === '') {
      setError('email cannot be blank');
      return;
    }

    if (!validateEmail()) {
      setError('email provided is invalid!');
      return;
    }

    try {
      const response = await preBooking(email);

      const {status, status_code} = response.data;

      if (status === SUCCESS_KEY) {
        // upon success
        showToasts(SUCCESS_KEY, getSuccessMessage(status_code), 'success');
        logPreBookingEvent(email); // logging event

      } else if (status === ERROR_KEY) {
        showToasts(ERROR_KEY, getErrorMessage(status_code), 'error');
      }
      setEmail('');
      // eslint-disable-next-line no-catch-shadow, @typescript-eslint/no-shadow
    } catch (error: any) {
      setEmail('');
      showToasts(ERROR_KEY, getErrorMessage(error?.response?.data?.status_code), 'error');
    } finally {
      onClose();
    }
  }

  const image = require('assets/images/SignUpModal/background_sign_up.png');

  /**
   * close button
   */
  const closeButton = (
    <TouchableOpacity onPress={onClose} style={styles.closeButton}>
      <Image style={styles.closeIcon} source={require('assets/icons/X_white.png')} />
    </TouchableOpacity>
  );

  // responsiveness code
  let overridestyles = structuredClone(styles);
  const SCREEN_WIDTH = useWindowDimensions().width;

  // breakpoints and stylings
  if (SCREEN_WIDTH < 1024) {
    overridestyles.modalContent.justifyContent = 'flex-start';
    overridestyles.title.fontSize = 33;
  }
  if (SCREEN_WIDTH < 720) {
    overridestyles.title.fontSize = 30;
    overridestyles.modalContainer.padding = spacing['6'];
  }
  if (SCREEN_WIDTH < 640) {
    overridestyles.title.fontSize = 27;
    overridestyles.modalContainer.padding = spacing['4'];
  }
  if (SCREEN_WIDTH < 480) {
    overridestyles.title.fontSize = 24;
    overridestyles.description.fontSize = 14;
    overridestyles.modalContainer.padding = spacing['2'];
  }

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={[styles.modalContainer, overridestyles.modalContainer]}>
        <View style={[styles.modalContent, overridestyles.modalContent]}>
          {closeButton}
          <Text style={[styles.title, overridestyles.title]}>
            Be the <Text style={styles.highlightedText}>First</Text> to
            experience our portal
          </Text>
          <Text style={[styles.description, overridestyles.description]}>
            Our all-in-one developer portal is almost here! We're working hard
            to bring you the tools you need to simplify, streamline, and
            supercharge your workflow.
          </Text>
          <View style={styles.animatedContainer}>
            <AnimatedCircle duration={1500} entities={ENTITIES} />
            <Image
              source={image}
              style={[styles.centerImage, overridestyles.centerImage]}
            />
          </View>
          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>Email</Text>
            <TextInput
              autoFocus={false}
              value={email}
              onChange={(e: NativeSyntheticEvent<TextInputChangeEventData>) =>
                setEmail(e.nativeEvent.text)
              }
              style={styles.textInput}
              placeholder="enter your email address"
              keyboardType="email-address"
            />
            <Text style={styles.errorLabel}>{error}</Text>
          </View>
          <Button
            variant="primary"
            disabled={!(Boolean(email) && validateEmail())}
            text="Join the waitlist"
            onPress={handleSubmit}
            style={styles.submitButton}
          />
        </View>
      </View>
    </Modal>
  );
}

type stylesType = {
  modalContainer: ViewStyle;
  modalContent: ViewStyle;
  title: TextStyle;
  highlightedText: TextStyle;
  centerImage: ImageStyle;
  inputContainer: ViewStyle;
  description: TextStyle;
  inputLabel: TextStyle;
  textInput: TextStyle;
  closeButton: ViewStyle;
  animatedContainer: ViewStyle;
  errorLabel: TextStyle;
  submitButton: ViewStyle;
  closeIcon: ImageStyle;
};

const styles: stylesType = {
  closeIcon: {flex:1},
  submitButton: {
    maxWidth: 310,
    width: '100%',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: spacing['8'],
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#121212',
    flex: 0.9,
    alignItems: 'center',
    padding: spacing['8'],
    borderRadius: corner_radius['rounded-lg'],
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  title: {
    color: 'white',
    fontSize: 36,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 10,
  },
  highlightedText: {
    color: colors.palette.brandBlue,
  },
  closeButton: {
    width: 28,
    height: 28,
    borderRadius: 999,
    position: 'absolute',
    top: 10,
    right: 10,
    padding: spacing['2'],
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  description: {
    color: 'white',
    fontSize: 20,
    textAlign: 'center',
    marginBottom: 20,
    maxWidth: 900,
  },
  centerImage: {
    flex: 0.88,
    resizeMode: 'contain',
    marginBottom: 20,
  },
  inputContainer: {
    maxWidth: 546,
    width: '100%',
    marginBottom: 20,
  },
  inputLabel: {
    color: 'white',
    fontSize: 14,
    marginBottom: 5,
  },
  textInput: {
    backgroundColor: 'white',
    borderRadius: corner_radius['rounded-sm'],
    padding: 10,
    fontSize: 16,
    color: '#717998',
    fontWeight: '500',
    outline: '1px solid transparent',
  },
  animatedContainer: {
    maxWidth: 414,
    maxHeight: 266,
    width: '90%',
    height: 266,
    justifyContent: 'center',
    alignContent: 'center',
  },
  errorLabel: {
    color: colors.palette.errorRed,
    alignSelf: 'flex-start',
    fontWeight: '700',
  },
};
